import React, { useState, useEffect, useMemo, useRef, type ComponentType } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Package,
  Search,
  AlertTriangle,
  TrendingUp,
  X,
  DollarSign,
  RefreshCw,
  Download,
  AlertCircle,
  Layers,
  Eye,
  FileText,
  ScrollText,
  Landmark,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import SearchableSelect, { type SearchableSelectOption } from '../components/SearchableSelect';
import InventoryMovementTotalsBar from '../components/transactions/InventoryMovementTotalsBar';
import { getStockValuation } from '../utils/reportApi';
import { getItems, getItemById } from '../utils/itemApi';
import { ApiValidationError } from '../types/auth';
import type { Item } from '../types/item';
import type { StockValuationReport } from '../types/report';
import type { PaginationMeta } from '../types/journalEntry';
import { formatCurrency, formatQuantity } from '../utils/formatCurrency';
import { getCompany } from '../utils/companyApi';
import {
  getAllowNegativeInventory,
  hasOpenBackorders,
  isOversoldItem,
  parseInventoryQty,
} from '../utils/negativeInventory';
import {
  canSetStartingValue,
  canViewStartingValue,
} from '../utils/inventoryStartingValueForm';
import { todayLocalIso } from '../utils/localDate';
import {
  computeStockValuationTotals,
  stockValuationRowKey,
} from '../utils/stockValuationSelection';

type InventoryTab = 'stock-levels' | 'stock-valuation';

const STOCK_LEVELS_PAGE_SIZE = 25;

const parseInventoryTab = (tabParam: string | null): InventoryTab =>
  tabParam === 'stock-valuation' ? 'stock-valuation' : 'stock-levels';

const inventoryTabs: {
  id: InventoryTab;
  label: string;
  icon: ComponentType<{ className?: string }>;
}[] = [
  { id: 'stock-levels', label: 'Stock Levels', icon: Package },
  { id: 'stock-valuation', label: 'Stock Valuation', icon: DollarSign },
];

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const itemToOption = (item: Item): SearchableSelectOption => ({
  value: item.id,
  label: item.name,
  description: item.sku,
});

const Inventory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseInventoryTab(searchParams.get('tab'));

  const setActiveTab = (tab: InventoryTab) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'stock-valuation') {
          next.set('tab', 'stock-valuation');
        } else {
          next.delete('tab');
        }
        return next;
      },
      { replace: true }
    );
  };
  const [stockLevelItems, setStockLevelItems] = useState<Item[]>([]);
  const [stockLevelsMeta, setStockLevelsMeta] = useState<PaginationMeta | null>(null);
  const [stockLevelsPage, setStockLevelsPage] = useState(1);
  const [stockSearchQuery, setStockSearchQuery] = useState('');
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [stockValuation, setStockValuation] = useState<StockValuationReport | null>(null);
  const [stockValuationLoading, setStockValuationLoading] = useState(false);
  const [stockValuationError, setStockValuationError] = useState<string | null>(null);
  const [selectedValuationItemId, setSelectedValuationItemId] = useState<number | null>(null);
  const [valuationItemOptions, setValuationItemOptions] = useState<SearchableSelectOption[]>([]);
  const [valuationItemSearchInput, setValuationItemSearchInput] = useState('');
  const [valuationItemSearchLoading, setValuationItemSearchLoading] = useState(false);
  const [selectedValuationItemOption, setSelectedValuationItemOption] =
    useState<SearchableSelectOption | null>(null);
  const selectedValuationItemOptionRef = useRef<SearchableSelectOption | null>(null);
  const valuationItemLookupRef = useRef<Map<number, Item>>(new Map());
  const [includeLayers, setIncludeLayers] = useState(true);
  const [valuationSearchQuery, setValuationSearchQuery] = useState('');
  const [showValuationLayers, setShowValuationLayers] = useState<{ [key: number]: boolean }>({});
  const [allowNegativeInventory, setAllowNegativeInventory] = useState(() =>
    getAllowNegativeInventory()
  );
  const [selectedValuationKeys, setSelectedValuationKeys] = useState<Set<string>>(() => new Set());
  const selectAllValuationCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getCompany()
      .then((company) => setAllowNegativeInventory(company.allow_negative_inventory ?? false))
      .catch(() => setAllowNegativeInventory(getAllowNegativeInventory()));
  }, []);

  const stockSummary = useMemo(() => {
    const totalQty = stockLevelItems.reduce(
      (sum, item) => sum + (parseFloat(item.qty_on_hand) || 0),
      0
    );
    const lowStock = stockLevelItems.filter((item) => {
      const qty = parseFloat(item.qty_on_hand) || 0;
      const reorder = parseFloat(item.reorder_point) || 0;
      return qty > 0 && qty <= reorder;
    }).length;
    const outOfStock = stockLevelItems.filter(
      (item) => (parseFloat(item.qty_on_hand) || 0) <= 0 && !isOversoldItem(item)
    ).length;
    const openBackorders = stockLevelItems.filter((item) => hasOpenBackorders(item)).length;

    return {
      totalItems: stockLevelsMeta?.total ?? stockLevelItems.length,
      totalQty,
      lowStock,
      outOfStock,
      openBackorders,
    };
  }, [stockLevelItems, stockLevelsMeta]);

  useEffect(() => {
    if (activeTab !== 'stock-levels') return;

    const timer = setTimeout(() => {
      setStockSearchQuery(searchQuery.trim());
      setStockLevelsPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, activeTab]);

  useEffect(() => {
    selectedValuationItemOptionRef.current = selectedValuationItemOption;
  }, [selectedValuationItemOption]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      setValuationItemSearchLoading(true);
      try {
        const result = await getItems({
          search: valuationItemSearchInput.trim() || undefined,
          item_type: 'INVENTORY',
          active: true,
          per_page: 20,
          page: 1,
        });
        for (const item of result.items) {
          valuationItemLookupRef.current.set(item.id, item);
        }
        const options = result.items.map(itemToOption);
        const selected = selectedValuationItemOptionRef.current;
        setValuationItemOptions(
          selected && !options.some((opt) => String(opt.value) === String(selected.value))
            ? [selected, ...options]
            : options
        );
      } catch {
        // silently ignore — dropdown just shows empty
      } finally {
        setValuationItemSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [valuationItemSearchInput]);

  useEffect(() => {
    if (activeTab === 'stock-levels') {
      fetchStockLevels(stockLevelsPage);
    }
    if (activeTab === 'stock-valuation') {
      fetchStockValuation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, stockLevelsPage, stockSearchQuery]);

  const fetchStockLevels = async (pageNum: number) => {
    try {
      setItemsLoading(true);
      setItemsError(null);
      const result = await getItems({
        item_type: 'INVENTORY',
        active: true,
        search: stockSearchQuery || undefined,
        per_page: STOCK_LEVELS_PAGE_SIZE,
        page: pageNum,
      });
      setStockLevelItems(result.items);
      setStockLevelsMeta(result.meta);
      setStockLevelsPage(result.meta.current_page);
    } catch (err) {
      setItemsError(err instanceof Error ? err.message : 'Failed to fetch inventory items');
      setStockLevelItems([]);
      setStockLevelsMeta(null);
    } finally {
      setItemsLoading(false);
    }
  };

  const fetchStockValuation = async () => {
    try {
      setStockValuationLoading(true);
      setStockValuationError(null);
      const data = await getStockValuation({
        item_id: selectedValuationItemId ?? undefined,
        include_layers: includeLayers,
      });
      setStockValuation(data);
      setShowValuationLayers({});
    } catch (err) {
      if (err instanceof ApiValidationError) {
        const firstError = Object.values(err.errors).flat()[0];
        setStockValuationError(firstError || err.message);
      } else {
        setStockValuationError(
          err instanceof Error ? err.message : 'Failed to fetch stock valuation'
        );
      }
      setStockValuation(null);
    } finally {
      setStockValuationLoading(false);
    }
  };

  const exportStockValuation = () => {
    if (!stockValuation) return;

    const rows = [
      ['Stock Valuation Report', `Method: ${stockValuation.valuation_method}`],
      [],
      ['SKU', 'Name', 'UOM', 'Qty on Hand', 'Layer Qty', 'Avg Unit Cost', 'Total Value'],
      ...stockValuation.items.map((item) => [
        item.sku,
        item.name,
        item.unit_of_measure,
        item.qty_on_hand,
        item.layer_qty,
        item.average_unit_cost,
        item.total_value,
      ]),
      [],
      ['Totals', '', '', stockValuation.totals.qty_on_hand, '', '', stockValuation.totals.total_value],
    ];

    const csv = rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `stock-valuation-${todayLocalIso()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const filteredValuationItems = useMemo(() => {
    return (stockValuation?.items ?? []).filter((item) => {
      if (!valuationSearchQuery.trim()) return true;
      const query = valuationSearchQuery.toLowerCase();
      return (
        item.sku.toLowerCase().includes(query) || item.name.toLowerCase().includes(query)
      );
    });
  }, [stockValuation?.items, valuationSearchQuery]);

  const valuationPageTotals = useMemo(
    () => computeStockValuationTotals(filteredValuationItems),
    [filteredValuationItems]
  );

  const visibleValuationRowKeys = useMemo(
    () => filteredValuationItems.map(stockValuationRowKey),
    [filteredValuationItems]
  );

  const selectedValuationRows = useMemo(
    () => filteredValuationItems.filter((item) => selectedValuationKeys.has(stockValuationRowKey(item))),
    [filteredValuationItems, selectedValuationKeys]
  );

  const selectedValuationTotals = useMemo(
    () => computeStockValuationTotals(selectedValuationRows),
    [selectedValuationRows]
  );

  const allValuationSelected =
    visibleValuationRowKeys.length > 0 &&
    visibleValuationRowKeys.every((key) => selectedValuationKeys.has(key));

  const someValuationSelected = visibleValuationRowKeys.some((key) =>
    selectedValuationKeys.has(key)
  );

  const toggleValuationRow = (key: string, checked: boolean) => {
    setSelectedValuationKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const toggleSelectAllValuation = (checked: boolean) => {
    setSelectedValuationKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const key of visibleValuationRowKeys) {
          next.add(key);
        }
      } else {
        for (const key of visibleValuationRowKeys) {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const clearValuationSelection = () => setSelectedValuationKeys(new Set());

  useEffect(() => {
    setSelectedValuationKeys(new Set());
  }, [valuationSearchQuery, selectedValuationItemId, includeLayers, stockValuation]);

  useEffect(() => {
    const visibleKeySet = new Set(visibleValuationRowKeys);
    setSelectedValuationKeys((prev) => {
      const pruned = new Set([...prev].filter((key) => visibleKeySet.has(key)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [visibleValuationRowKeys]);

  useEffect(() => {
    const input = selectAllValuationCheckboxRef.current;
    if (input) {
      input.indeterminate = someValuationSelected && !allValuationSelected;
    }
  }, [someValuationSelected, allValuationSelected]);

  const valuationTotalsBarMode = selectedValuationTotals.count > 0 ? 'selected' : 'page';
  const valuationTotalsBarData =
    valuationTotalsBarMode === 'selected' ? selectedValuationTotals : valuationPageTotals;
  const valuationTotalsBarPageSuffix =
    filteredValuationItems.length < (stockValuation?.items.length ?? 0) ? ' in view' : '';
  const showValuationTotalsBar =
    !stockValuationLoading && stockValuation !== null && filteredValuationItems.length > 0;

  const valuationTableColCount = includeLayers ? 8 : 7;

  const toggleValuationLayers = (itemId: number) => {
    setShowValuationLayers((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const handleValuationItemChange = async (value: number | string) => {
    const id = Number(value);
    const selectedId = id > 0 ? id : null;
    setSelectedValuationItemId(selectedId);

    if (!selectedId) {
      setSelectedValuationItemOption(null);
      selectedValuationItemOptionRef.current = null;
      return;
    }

    let item = valuationItemLookupRef.current.get(selectedId);
    if (!item) {
      item = await getItemById(selectedId).catch(() => undefined);
      if (item) {
        valuationItemLookupRef.current.set(selectedId, item);
      }
    }

    if (item) {
      const option = itemToOption(item);
      setSelectedValuationItemOption(option);
      selectedValuationItemOptionRef.current = option;
      setValuationItemOptions((prev) =>
        prev.some((opt) => String(opt.value) === String(selectedId)) ? prev : [option, ...prev]
      );
    }
  };

  const getQtyStatusColor = (qty: string, reorderPoint: string) => {
    const qtyNum = parseFloat(qty) || 0;
    const reorderNum = parseFloat(reorderPoint) || 0;
    if (qtyNum <= 0) return 'text-red-600';
    if (qtyNum <= reorderNum) return 'text-yellow-600';
    return 'text-green-600';
  };

  const renderStockLevels = () => {
    if (itemsLoading) {
      return (
        <div className="p-12 flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
        </div>
      );
    }

    return (
      <>
        {itemsError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between gap-4">
            <div className="flex gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
              <p className="text-red-800 text-sm">{itemsError}</p>
            </div>
            <button onClick={() => fetchStockLevels(stockLevelsPage)} className="text-sm text-red-700 underline shrink-0">
              Retry
            </button>
          </div>
        )}

        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Package className="h-6 w-6 text-slate-700" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Inventory Items</p>
                <p className="text-2xl font-semibold text-gray-900">{stockSummary.totalItems}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Qty on Hand</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {formatQuantity(stockSummary.totalQty)}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">This page</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Low Stock</p>
                <p className="text-2xl font-semibold text-gray-900">{stockSummary.lowStock}</p>
                <p className="text-xs text-gray-400 mt-0.5">This page</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Out of Stock</p>
                <p className="text-2xl font-semibold text-gray-900">{stockSummary.outOfStock}</p>
                <p className="text-xs text-gray-400 mt-0.5">This page</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="overflow-x-auto w-full">
            <table className="w-full min-w-[800px] divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    UOM
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Qty on Hand
                  </th>
                  {allowNegativeInventory && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Backorder
                    </th>
                  )}
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reorder Point
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  {canViewStartingValue() && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stockLevelItems.length === 0 ? (
                  <tr>
                    <td colSpan={canViewStartingValue() ? (allowNegativeInventory ? 8 : 7) : (allowNegativeInventory ? 7 : 6)} className="px-6 py-12 text-center text-gray-500">
                      {stockSearchQuery
                        ? 'No items match your search.'
                        : 'No inventory items found.'}
                    </td>
                  </tr>
                ) : (
                  stockLevelItems.map((item) => {
                    const qty = parseFloat(item.qty_on_hand) || 0;
                    const reorder = parseFloat(item.reorder_point) || 0;
                    const oversold = isOversoldItem(item);
                    const isOutOfStock = qty <= 0 && !oversold;
                    const isLowStock = !isOutOfStock && !oversold && qty <= reorder;
                    const showViewOpening =
                      canViewStartingValue() && item.item_type === 'INVENTORY';
                    const showSetOpening =
                      canSetStartingValue() && item.item_type === 'INVENTORY' && isOutOfStock;

                    return (
                      <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                              <Package className="h-6 w-6 text-slate-700" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{item.name}</div>
                              <div className="text-sm text-gray-500">SKU: {item.sku}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatLabel(item.unit_of_measure)}
                        </td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap text-sm text-right font-mono font-medium ${
                            oversold
                              ? 'text-rose-700'
                              : getQtyStatusColor(item.qty_on_hand, item.reorder_point)
                          }`}
                        >
                          {formatQuantity(item.qty_on_hand)}
                        </td>
                        {allowNegativeInventory && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-mono text-gray-700">
                            {parseInventoryQty(item.backorder_qty) > 0
                              ? formatQuantity(item.backorder_qty ?? '0')
                              : '—'}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-mono text-gray-600">
                          {formatQuantity(item.reorder_point)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-mono text-gray-900">
                          {formatCurrency(item.unit_price)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              oversold
                                ? 'bg-rose-100 text-rose-800'
                                : isOutOfStock
                                  ? 'bg-red-100 text-red-800'
                                  : isLowStock
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {oversold
                              ? 'OVERSOLD'
                              : isOutOfStock
                                ? 'OUT OF STOCK'
                                : isLowStock
                                  ? 'LOW STOCK'
                                  : 'IN STOCK'}
                          </span>
                        </td>
                        {canViewStartingValue() && (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                            {showViewOpening || showSetOpening ? (
                              <div className="inline-flex flex-wrap items-center justify-end gap-2">
                                {showViewOpening && (
                                  <Link
                                    to={`/inventory-starting-values/${item.id}`}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50"
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                    View opening stock
                                  </Link>
                                )}
                                {showSetOpening && (
                                  <Link
                                    to={`/manage-inventory-starting-values?itemId=${item.id}`}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-cyan-800 border border-cyan-200 rounded-lg hover:bg-cyan-50"
                                  >
                                    <Layers className="h-3.5 w-3.5" />
                                    Set opening stock
                                  </Link>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {stockLevelsMeta && stockLevelsMeta.last_page > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Page {stockLevelsMeta.current_page} of {stockLevelsMeta.last_page}
                {' · '}
                {stockLevelsMeta.total} items
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setStockLevelsPage((p) => Math.max(1, p - 1))}
                  disabled={stockLevelsMeta.current_page <= 1 || itemsLoading}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setStockLevelsPage((p) => p + 1)}
                  disabled={
                    stockLevelsMeta.current_page >= stockLevelsMeta.last_page || itemsLoading
                  }
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderStockValuation = () => (
    <div className="flex flex-col min-h-0 flex-1">
      {stockValuationError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between gap-4">
          <div className="flex gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
            <p className="text-red-800 text-sm">{stockValuationError}</p>
          </div>
          <button onClick={fetchStockValuation} className="text-sm text-red-700 underline shrink-0">
            Retry
          </button>
        </div>
      )}

      {stockValuationLoading && (
        <div className="p-12 flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
        </div>
      )}

      {!stockValuationLoading && stockValuation && (
        <div className="flex flex-col min-h-0 flex-1">
          <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Layers className="h-6 w-6 text-slate-700" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Valuation Method</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {stockValuation.valuation_method}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Package className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Qty on Hand</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatQuantity(stockValuation.totals.qty_on_hand)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-emerald-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Inventory Value</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(stockValuation.totals.total_value)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="overflow-x-auto w-full">
              {filteredValuationItems.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  {valuationSearchQuery
                    ? 'No items match your search.'
                    : 'No inventory items with FIFO value for the selected filters.'}
                </div>
              ) : (
                <table className="w-full min-w-[900px] divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 w-10">
                        <input
                          ref={selectAllValuationCheckboxRef}
                          type="checkbox"
                          checked={allValuationSelected}
                          onChange={(e) => toggleSelectAllValuation(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-slate-800"
                          aria-label="Select all visible rows"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Item
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        UOM
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Qty on Hand
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Layer Qty
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Avg Unit Cost
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Value
                      </th>
                      {includeLayers && (
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Layers
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredValuationItems.map((item) => {
                      const rowKey = stockValuationRowKey(item);
                      const isCheckboxSelected = selectedValuationKeys.has(rowKey);
                      return (
                      <React.Fragment key={item.item_id}>
                        <tr
                          className={`hover:bg-gray-50 transition-colors${
                            isCheckboxSelected ? ' bg-slate-50/60' : ''
                          }`}
                        >
                          <td className="px-4 py-4 w-10" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isCheckboxSelected}
                              onChange={(e) => toggleValuationRow(rowKey, e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-slate-800"
                              aria-label={`Select ${item.name}`}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{item.name}</div>
                            <div className="text-sm text-gray-500">SKU: {item.sku}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatLabel(item.unit_of_measure)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-mono text-gray-900">
                            {formatQuantity(item.qty_on_hand)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-mono text-gray-900">
                            {formatQuantity(item.layer_qty)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-mono text-gray-900">
                            {formatCurrency(item.average_unit_cost)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-mono font-medium text-gray-900">
                            {formatCurrency(item.total_value)}
                          </td>
                          {includeLayers && (
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              {item.layers && item.layers.length > 0 ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleValuationLayers(item.item_id);
                                  }}
                                  className="text-slate-700 hover:text-slate-900 transition-colors p-1 rounded hover:bg-slate-50 text-sm"
                                >
                                  {showValuationLayers[item.item_id] ? 'Hide' : 'View'} (
                                  {item.layers.length})
                                </button>
                              ) : (
                                <span className="text-gray-400 text-sm">—</span>
                              )}
                            </td>
                          )}
                        </tr>
                        {includeLayers &&
                          showValuationLayers[item.item_id] &&
                          item.layers &&
                          item.layers.length > 0 && (
                            <tr>
                              <td colSpan={valuationTableColCount} className="px-6 py-4 bg-gray-50">
                                <div className="space-y-2">
                                  <h4 className="font-medium text-gray-900 text-sm">FIFO Layers</h4>
                                  <div className="overflow-x-auto">
                                    <table className="w-full min-w-[600px] text-sm">
                                      <thead>
                                        <tr className="text-left text-xs text-gray-500 uppercase">
                                          <th className="pb-2 pr-4">Date Acquired</th>
                                          <th className="pb-2 pr-4 text-right">Qty Remaining</th>
                                          <th className="pb-2 pr-4 text-right">Unit Cost</th>
                                          <th className="pb-2 text-right">Layer Value</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-200">
                                        {item.layers.map((layer) => (
                                          <tr key={layer.layer_id}>
                                            <td className="py-2 pr-4 text-gray-700">
                                              {formatDate(layer.date_acquired)}
                                            </td>
                                            <td className="py-2 pr-4 text-right font-mono text-gray-900">
                                              {formatQuantity(layer.qty_remaining)}
                                            </td>
                                            <td className="py-2 pr-4 text-right font-mono text-gray-900">
                                              {formatCurrency(layer.unit_cost)}
                                            </td>
                                            <td className="py-2 text-right font-mono font-medium text-gray-900">
                                              {formatCurrency(layer.layer_value)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          </div>

          {showValuationTotalsBar && (
            <InventoryMovementTotalsBar
              mode={valuationTotalsBarMode}
              count={valuationTotalsBarData.count}
              qtyChange={valuationTotalsBarData.qtyOnHand}
              valueChange={valuationTotalsBarData.totalValue}
              hideQty={false}
              qtyLabel="Qty on hand"
              valueLabel="Total value"
              pageCountSuffix={valuationTotalsBarPageSuffix}
              onClear={
                valuationTotalsBarMode === 'selected' ? clearValuationSelection : undefined
              }
            />
          )}
        </div>
      )}

      {!stockValuationLoading && !stockValuation && !stockValuationError && (
        <div className="py-12 text-center text-gray-500">
          Click &quot;Run Report&quot; to generate the stock valuation.
        </div>
      )}
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="bg-white shadow border-b border-gray-200">
          <div className="px-6 py-5 space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Monitor stock levels and FIFO inventory valuation
                </p>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Link
                  to="/inventory-transactions"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg"
                >
                  <ScrollText className="h-4 w-4" />
                  Inventory Transactions
                </Link>
                <Link
                  to="/reports"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg"
                >
                  <FileText className="h-4 w-4" />
                  Reports
                </Link>
                <Link
                  to="/reports/bank-collateral-stock"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg"
                >
                  <Landmark className="h-4 w-4" />
                  Bank Collateral
                </Link>
                {canSetStartingValue() && (
                  <Link
                    to="/inventory-opening-stock-import"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 text-sm font-medium"
                  >
                    <Layers className="h-4 w-4" />
                    Bulk Opening Stock
                  </Link>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-1">
              {inventoryTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-slate-800 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {activeTab === 'stock-levels' && (
              <div className="relative max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Search by SKU or item name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-800 focus:border-slate-600 bg-white text-sm"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}

            {activeTab === 'stock-valuation' && (
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder="Search valuation by SKU or name..."
                    value={valuationSearchQuery}
                    onChange={(e) => setValuationSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-9 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-800 focus:border-slate-600 bg-white text-sm"
                  />
                  {valuationSearchQuery && (
                    <button
                      type="button"
                      onClick={() => setValuationSearchQuery('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <SearchableSelect
                  className="w-full sm:w-64"
                  value={selectedValuationItemId ?? 0}
                  onChange={(itemId) => void handleValuationItemChange(itemId)}
                  placeholder="All items"
                  searchPlaceholder="Search by name or SKU..."
                  clearValue={0}
                  options={valuationItemOptions}
                  onSearchChange={setValuationItemSearchInput}
                  isLoading={valuationItemSearchLoading}
                />
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={includeLayers}
                    onChange={(e) => setIncludeLayers(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  Include FIFO layers
                </label>
                <button
                  type="button"
                  onClick={fetchStockValuation}
                  disabled={stockValuationLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-60"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${stockValuationLoading ? 'animate-spin' : ''}`}
                  />
                  Run Report
                </button>
                <button
                  type="button"
                  onClick={exportStockValuation}
                  disabled={!stockValuation}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  Export
                </button>
              </div>
            )}
          </div>
        </div>

        <div
          className={`flex-1 p-6 ${
            activeTab === 'stock-valuation'
              ? 'flex flex-col min-h-0 overflow-hidden'
              : 'overflow-auto'
          }`}
        >
          {activeTab === 'stock-levels' && renderStockLevels()}
          {activeTab === 'stock-valuation' && renderStockValuation()}
        </div>
      </div>
    </div>
  );
};

export default Inventory;
